'use client'

import type { PropsWithChildren } from 'react'
import { APP_CONFIG } from '@cocoonspace/shared/config'
import { facebookConfig } from '@cocoonspace/shared/config/facebook-config'
import { twitterConfig } from '@cocoonspace/shared/config/twitter-config'
import { useLingui } from '@lingui/react/macro'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'

import { useLocale } from '~/hooks/use-locale.hook'
import { defaultLocale, locales, stripLocaleFromPath } from '~/i18n/routing'

interface SeoTagsProps {
	title: string
	description?: string
	imageUrl?: string
	jsonLd?: Record<string, any>
}

const twicDomain = process.env.NEXT_PUBLIC_TWICPICS_DOMAIN
const twicPath = process.env.NEXT_PUBLIC_TWICPICS_PATH

const getFacebookMetas = ({
	title,
	description,
	imageUrl,
	url,
	locale = 'fr',
}: Record<string, string>) => {
	const defaultImagePath = `${twicDomain}/${twicPath}${facebookConfig.metaImage.url}?twic=v1/resize=${facebookConfig.metaImage.width}`
	const ogLocale = locale === 'en' ? 'en_US' : 'fr_FR'

	return [
		{ content: process.env.NEXT_PUBLIC_FACEBOOK_APPID, property: 'fb:app_id' },
		{ content: ogLocale, property: 'og:locale' },
		{ content: 'website', property: 'og:type' },
		{ content: url, property: 'og:url' },
		{
			content: 'Cocoon Space - Espaces de travail cosy à louer à la demande',
			property: 'og:site_name',
		},
		{ content: title, property: 'og:title' },
		{ content: description, property: 'og:description' },
		{
			content: imageUrl || defaultImagePath,
			property: 'og:image',
		},
		{ content: facebookConfig.metaImage.type, property: 'og:image:type' },
		{
			content: `${facebookConfig.metaImage.width}`,
			property: 'og:image:width',
		},
		{
			content: `${facebookConfig.metaImage.height}`,
			property: 'og:image:height',
		},
	]
}

const getTwitterMetas = ({
	title,
	description,
	imageUrl,
}: Record<string, string>) => {
	const defaultTitterImg = `${twicDomain}/${twicPath}${twitterConfig.metaImage.url}?twic=v1/resize=${twitterConfig.metaImage.width}`

	return [
		{ content: 'summary_large_image', name: 'twitter:card' },
		{ content: twitterConfig.handleName, name: 'twitter:site' },
		{ content: title, name: 'twitter:title' },
		{ content: description, name: 'twitter:description' },
		{
			content: imageUrl || defaultTitterImg,
			name: 'twitter:image',
		},
	]
}

const upsertMeta = (
	selector: string,
	attributes: Record<string, string | undefined>,
) => {
	const element =
		document.head.querySelector<HTMLMetaElement>(selector) ??
		document.createElement('meta')

	for (const [key, value] of Object.entries(attributes)) {
		if (value !== undefined) {
			element.setAttribute(key, value)
		}
	}

	if (!element.parentNode) {
		document.head.appendChild(element)
	}
}

const upsertLink = (
	selector: string,
	attributes: Record<string, string | undefined>,
) => {
	const element =
		document.head.querySelector<HTMLLinkElement>(selector) ??
		document.createElement('link')

	for (const [key, value] of Object.entries(attributes)) {
		if (value !== undefined) {
			element.setAttribute(key, value)
		}
	}

	if (!element.parentNode) {
		document.head.appendChild(element)
	}
}

const upsertJsonLd = (jsonLd: Record<string, any>) => {
	const selector = 'script[data-seo-json-ld="true"]'
	const element =
		document.head.querySelector<HTMLScriptElement>(selector) ??
		document.createElement('script')

	element.type = 'application/ld+json'
	element.dataset.seoJsonLd = 'true'
	element.textContent = JSON.stringify(jsonLd)

	if (!element.parentNode) {
		document.head.appendChild(element)
	}
}

const removeJsonLd = () => {
	document.head
		.querySelector('script[data-seo-json-ld="true"]')
		?.remove()
}

export const SeoTags = ({
	title,
	description,
	imageUrl,
	children,
	jsonLd,
}: PropsWithChildren<SeoTagsProps>) => {
	const { t } = useLingui()
	const locale = useLocale()
	const pathname = usePathname() ?? ''
	const searchParams = useSearchParams()

	const defaultDescription = t`Dès 15€/heure, réservez en ligne pour 1h ou plus, une salle de réunion ou un bureau calme, design et accessible sur Paris de 8h à 23h et week-end. De 1 à 30 personnes.`

	const pathWithoutQuery = stripLocaleFromPath(pathname).split('?')[0]
	const asPath = `${pathWithoutQuery}${searchParams.size ? `?${searchParams}` : ''}`
	const canonical = `${APP_CONFIG.appUrl}${pathWithoutQuery === '/' ? '' : pathWithoutQuery}`
	const resolvedDescription = description || defaultDescription

	useEffect(() => {
		document.title = title

		upsertMeta('meta[name="description"]', {
			name: 'description',
			content: resolvedDescription,
		})

		upsertLink('link[rel="canonical"]', {
			rel: 'canonical',
			href: canonical,
		})

		for (const loc of locales) {
			const hrefLang = loc === defaultLocale ? 'fr-FR' : 'en-US'
			const href =
				loc === defaultLocale
					? canonical
					: `${APP_CONFIG.appUrl}/${loc}${pathWithoutQuery === '/' ? '' : pathWithoutQuery}`

			upsertLink(`link[rel="alternate"][hreflang="${hrefLang}"]`, {
				rel: 'alternate',
				hrefLang,
				href,
			})
		}

		upsertLink('link[rel="alternate"][hreflang="x-default"]', {
			rel: 'alternate',
			hrefLang: 'x-default',
			href: canonical,
		})

		for (const meta of getFacebookMetas({
			title,
			description: resolvedDescription,
			imageUrl: imageUrl || '',
			url: `${APP_CONFIG.appUrl}${asPath}`,
			locale,
		})) {
			upsertMeta(`meta[property="${meta.property}"]`, {
				property: meta.property,
				content: meta.content,
			})
		}

		for (const meta of getTwitterMetas({
			title,
			description: resolvedDescription,
			imageUrl: imageUrl || '',
		})) {
			upsertMeta(`meta[name="${meta.name}"]`, {
				name: meta.name,
				content: meta.content,
			})
		}

		if (jsonLd) {
			upsertJsonLd(jsonLd)
		} else {
			removeJsonLd()
		}
	}, [
		asPath,
		canonical,
		imageUrl,
		jsonLd,
		locale,
		pathWithoutQuery,
		resolvedDescription,
		title,
	])

	return <>{children}</>
}
